import React, { useState, useEffect, useRef, useMemo } from 'react';
import { adminApi } from '../../services/adminApi';
import type { PropertyDto, PropertyResolutionDto } from '../../services/adminApi.types';
import './PropertiesTab.css';

// Constants for filtering and expansion
const FILTER_DEBOUNCE_MS = 300;
const LONG_VALUE_THRESHOLD = 100;

export const PropertiesTab: React.FC = () => {
    const [properties, setProperties] = useState<PropertyDto[]>([]);
    const [activeProfiles, setActiveProfiles] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

    // Property resolution state
    const [resolvingKey, setResolvingKey] = useState<string | null>(null);
    const [resolvedProperty, setResolvedProperty] = useState<PropertyResolutionDto | null>(null);
    const [resolving, setResolving] = useState(false);

    // Filters - immediate state for responsive UI
    const [sourceFilter, setSourceFilter] = useState('');
    const [profileFilter, setProfileFilter] = useState('');
    const [keyFilter, setKeyFilter] = useState('');
    const [valueFilter, setValueFilter] = useState('');

    // Debounced filters - used for actual filtering (300ms delay)
    const [debouncedSourceFilter, setDebouncedSourceFilter] = useState('');
    const [debouncedProfileFilter, setDebouncedProfileFilter] = useState('');
    const [debouncedKeyFilter, setDebouncedKeyFilter] = useState('');
    const [debouncedValueFilter, setDebouncedValueFilter] = useState('');

    // Dropdown visibility
    const [showSourceDropdown, setShowSourceDropdown] = useState(false);
    const [showProfileDropdown, setShowProfileDropdown] = useState(false);

    // Refs for click outside detection
    const sourceDropdownRef = useRef<HTMLDivElement>(null);
    const profileDropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        loadProperties();
    }, []);

    // Click outside to close dropdowns
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (sourceDropdownRef.current && !sourceDropdownRef.current.contains(event.target as Node)) {
                setShowSourceDropdown(false);
            }
            if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target as Node)) {
                setShowProfileDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Debounce filter inputs
    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSourceFilter(sourceFilter), FILTER_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [sourceFilter]);

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedProfileFilter(profileFilter), FILTER_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [profileFilter]);

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedKeyFilter(keyFilter), FILTER_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [keyFilter]);

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedValueFilter(valueFilter), FILTER_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [valueFilter]);

    const loadProperties = async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await adminApi.getProperties();
            setProperties(data.properties);
            setActiveProfiles(data.activeProfiles);
        } catch (err) {
            setError('Failed to load properties');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const toggleRowExpansion = (rowKey: string) => {
        setExpandedRows(prev => {
            const newSet = new Set(prev);
            if (newSet.has(rowKey)) {
                newSet.delete(rowKey);
            } else {
                newSet.add(rowKey);
            }
            return newSet;
        });
    };

    const handleResolveProperty = async (key: string) => {
        setResolvingKey(key);
        setResolving(true);
        setResolvedProperty(null);
        try {
            const resolution = await adminApi.resolveProperty(key);
            setResolvedProperty(resolution);
        } catch (err) {
            console.error('Failed to resolve property:', err);
            setError(`Failed to resolve property: ${key}`);
        } finally {
            setResolving(false);
        }
    };

    const closeResolutionDialog = () => {
        setResolvingKey(null);
        setResolvedProperty(null);
    };

    // Filter properties based on debounced input (prevents re-filtering on every keystroke)
    const filteredProperties = useMemo(() => {
        return properties.filter(prop => {
            const sourceMatch = !debouncedSourceFilter || prop.source.toLowerCase().includes(debouncedSourceFilter.toLowerCase());
            const profileMatch = !debouncedProfileFilter || (prop.profile && prop.profile.toLowerCase().includes(debouncedProfileFilter.toLowerCase()));
            const keyMatch = !debouncedKeyFilter || prop.key.toLowerCase().includes(debouncedKeyFilter.toLowerCase());
            const valueMatch = !debouncedValueFilter || (prop.value && prop.value.toLowerCase().includes(debouncedValueFilter.toLowerCase()));
            return sourceMatch && profileMatch && keyMatch && valueMatch;
        });
    }, [properties, debouncedSourceFilter, debouncedProfileFilter, debouncedKeyFilter, debouncedValueFilter]);

    // Get unique sources and profiles for filter dropdowns (memoized for performance)
    const uniqueSources = useMemo(() =>
        Array.from(new Set(properties.map(prop => prop.source))).sort(),
        [properties]
    );

    const uniqueProfiles = useMemo(() =>
        Array.from(new Set(properties.map(prop => prop.profile).filter(p => p !== null))).sort() as string[],
        [properties]
    );

    return (
        <div className="properties-tab">
            <div className="properties-header">
                <div className="header-title">
                    <h2>Spring Properties</h2>
                    {activeProfiles.length > 0 && (
                        <div className="active-profiles">
                            {activeProfiles.map(profile => (
                                <span key={profile} className="profile-tag">
                                    {profile}
                                </span>
                            ))}
                        </div>
                    )}
                </div>
                <button onClick={loadProperties} disabled={loading}>
                    {loading ? 'Loading...' : 'Refresh'}
                </button>
            </div>

            <div className="properties-info">
                <p>
                    Showing {filteredProperties.length} of {properties.length} properties.
                    Sensitive values (passwords, tokens, api-keys) are masked.
                </p>
            </div>

            {error && <div className="error-message">{error}</div>}

            <div className="properties-filters">
                <div className="filter-group" ref={sourceDropdownRef}>
                    <label htmlFor="source-filter">Source:</label>
                    <div className="autocomplete-wrapper">
                        <input
                            id="source-filter"
                            type="text"
                            value={sourceFilter}
                            onChange={(e) => setSourceFilter(e.target.value)}
                            onFocus={() => setShowSourceDropdown(true)}
                            placeholder="Filter by source..."
                            className="autocomplete-input"
                            role="combobox"
                            aria-expanded={showSourceDropdown}
                            aria-controls="source-dropdown"
                            aria-autocomplete="list"
                            aria-label="Filter properties by source"
                        />
                        {showSourceDropdown && uniqueSources.length > 0 && (
                            <div
                                id="source-dropdown"
                                className="autocomplete-dropdown"
                                role="listbox"
                                aria-label="Source options"
                            >
                                <div
                                    className="autocomplete-item"
                                    role="option"
                                    tabIndex={0}
                                    onClick={() => {
                                        setSourceFilter('');
                                        setShowSourceDropdown(false);
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            setSourceFilter('');
                                            setShowSourceDropdown(false);
                                        }
                                    }}
                                >
                                    All Sources
                                </div>
                                {uniqueSources
                                    .filter(source => source.toLowerCase().includes(sourceFilter.toLowerCase()))
                                    .map(source => (
                                        <div
                                            key={source}
                                            className="autocomplete-item"
                                            role="option"
                                            tabIndex={0}
                                            onClick={() => {
                                                setSourceFilter(source);
                                                setShowSourceDropdown(false);
                                            }}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    e.preventDefault();
                                                    setSourceFilter(source);
                                                    setShowSourceDropdown(false);
                                                }
                                            }}
                                        >
                                            {source}
                                        </div>
                                    ))}
                            </div>
                        )}
                    </div>
                </div>

                <div className="filter-group" ref={profileDropdownRef}>
                    <label htmlFor="profile-filter">Profile:</label>
                    <div className="autocomplete-wrapper">
                        <input
                            id="profile-filter"
                            type="text"
                            value={profileFilter}
                            onChange={(e) => setProfileFilter(e.target.value)}
                            onFocus={() => setShowProfileDropdown(true)}
                            placeholder="Filter by profile..."
                            className="autocomplete-input"
                            role="combobox"
                            aria-expanded={showProfileDropdown}
                            aria-controls="profile-dropdown"
                            aria-autocomplete="list"
                            aria-label="Filter properties by profile"
                        />
                        {showProfileDropdown && uniqueProfiles.length > 0 && (
                            <div
                                id="profile-dropdown"
                                className="autocomplete-dropdown"
                                role="listbox"
                                aria-label="Profile options"
                            >
                                <div
                                    className="autocomplete-item"
                                    role="option"
                                    tabIndex={0}
                                    onClick={() => {
                                        setProfileFilter('');
                                        setShowProfileDropdown(false);
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            setProfileFilter('');
                                            setShowProfileDropdown(false);
                                        }
                                    }}
                                >
                                    All Profiles
                                </div>
                                {uniqueProfiles
                                    .filter(profile => profile.toLowerCase().includes(profileFilter.toLowerCase()))
                                    .map(profile => (
                                        <div
                                            key={profile}
                                            className="autocomplete-item"
                                            role="option"
                                            tabIndex={0}
                                            onClick={() => {
                                                setProfileFilter(profile);
                                                setShowProfileDropdown(false);
                                            }}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    e.preventDefault();
                                                    setProfileFilter(profile);
                                                    setShowProfileDropdown(false);
                                                }
                                            }}
                                        >
                                            {profile}
                                        </div>
                                    ))}
                            </div>
                        )}
                    </div>
                </div>

                <div className="filter-group">
                    <label htmlFor="key-filter">Key:</label>
                    <input
                        id="key-filter"
                        type="text"
                        value={keyFilter}
                        onChange={(e) => setKeyFilter(e.target.value)}
                        placeholder="Filter by key..."
                        aria-label="Filter properties by key"
                    />
                </div>

                <div className="filter-group">
                    <label htmlFor="value-filter">Value:</label>
                    <input
                        id="value-filter"
                        type="text"
                        value={valueFilter}
                        onChange={(e) => setValueFilter(e.target.value)}
                        placeholder="Filter by value..."
                        aria-label="Filter properties by value"
                    />
                </div>
            </div>

            {loading ? (
                <div className="loading">Loading properties...</div>
            ) : (
                <div className="properties-table-container">
                    <table className="properties-table">
                        <thead>
                            <tr>
                                <th>Source</th>
                                <th>Profile</th>
                                <th>Key</th>
                                <th>Value</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredProperties.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className="no-properties">
                                        No properties found
                                    </td>
                                </tr>
                            ) : (
                                filteredProperties.map((prop) => {
                                    const rowKey = `${prop.source}-${prop.profile || 'null'}-${prop.key}`;
                                    const isExpanded = expandedRows.has(rowKey);
                                    const valueLength = prop.value?.length || 0;
                                    const isLongValue = valueLength > LONG_VALUE_THRESHOLD;

                                    return (
                                        <tr key={rowKey}>
                                            <td className="source-cell">{prop.source}</td>
                                            <td className="profile-cell">
                                                {prop.profile ? (
                                                    <span className="profile-badge">{prop.profile}</span>
                                                ) : (
                                                    <em className="no-profile">—</em>
                                                )}
                                            </td>
                                            <td className="key-cell">{prop.key}</td>
                                            <td className="value-cell">
                                                <div className={`value-content ${isExpanded ? 'expanded' : 'collapsed'} ${isLongValue ? 'long-value' : ''}`}>
                                                    {prop.value !== null ? prop.value : <em>null</em>}
                                                </div>
                                                {isLongValue && (
                                                    <button
                                                        className="expand-button"
                                                        onClick={() => toggleRowExpansion(rowKey)}
                                                        aria-expanded={isExpanded}
                                                        aria-label={isExpanded ? `Collapse value for ${prop.key}` : `Expand value for ${prop.key}`}
                                                    >
                                                        {isExpanded ? '▲ Collapse' : '▼ Expand'}
                                                    </button>
                                                )}
                                            </td>
                                            <td className="actions-cell">
                                                <button
                                                    className="resolve-button"
                                                    onClick={() => handleResolveProperty(prop.key)}
                                                    aria-label={`Resolve value for ${prop.key}`}
                                                    title="Resolve actual Spring Environment value"
                                                >
                                                    🔍
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Property Resolution Dialog */}
            {resolvingKey && (
                <div className="resolution-dialog-overlay" onClick={closeResolutionDialog}>
                    <div className="resolution-dialog" onClick={(e) => e.stopPropagation()}>
                        <div className="resolution-dialog-header">
                            <h3>Property Resolution</h3>
                            <button className="close-button" onClick={closeResolutionDialog}>✕</button>
                        </div>
                        <div className="resolution-dialog-content">
                            {resolving ? (
                                <div className="resolving">Resolving property...</div>
                            ) : resolvedProperty ? (
                                <>
                                    <div className="resolution-field">
                                        <label>Key:</label>
                                        <code>{resolvedProperty.key}</code>
                                    </div>
                                    <div className="resolution-field">
                                        <label>Resolved Value:</label>
                                        <code className="resolved-value">{resolvedProperty.resolvedValue ?? <em>null</em>}</code>
                                    </div>
                                    <div className="resolution-field">
                                        <label>Raw Value:</label>
                                        <code>{resolvedProperty.rawValue ?? <em>null</em>}</code>
                                    </div>
                                    <div className="resolution-field">
                                        <label>Source:</label>
                                        <span>{resolvedProperty.source ?? <em>N/A</em>}</span>
                                    </div>
                                    <div className="resolution-field">
                                        <label>Has Placeholders:</label>
                                        <span className={resolvedProperty.hasPlaceholders ? 'badge-yes' : 'badge-no'}>
                                            {resolvedProperty.hasPlaceholders ? 'Yes' : 'No'}
                                        </span>
                                    </div>
                                    <div className="resolution-field">
                                        <label>Resolved:</label>
                                        <span className={resolvedProperty.resolved ? 'badge-yes' : 'badge-no'}>
                                            {resolvedProperty.resolved ? 'Yes' : 'No'}
                                        </span>
                                    </div>
                                    <div className="resolution-field">
                                        <label>Active Profiles:</label>
                                        <div className="profile-badges">
                                            {resolvedProperty.activeProfiles.map(profile => (
                                                <span key={profile} className="profile-badge">{profile}</span>
                                            ))}
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <div className="error">Failed to resolve property</div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
